import React from 'react';
import PropTypes from 'prop-types';
import { i18next } from '@yola/ws-sdk';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import utils from 'src/js/modules/utils';
import helpers from '../helpers';
import constants from '../constants';

const { Stack, HorizontalStack, Box, ControlGroup, CheckboxField, InputGroupField, ActionButton } =
  designSystem;
const { useFieldArray, useController, useWatch } = reactHookForm;

const { createRequiredValidator } = utils.validators;
const {
  common: { LABEL_INPUT, PLACEHOLDER_INPUT, OPTIONS_INPUT },
  fieldControlNames,
} = constants;

const OptionsFieldControls = ({
  control,
  placeholders,
  minOptionsCount,
  onChangeRequiredFieldControl = null,
}) => {
  const { formState } = useController({
    name: fieldControlNames.REQUIRED,
    control,
  });
  const { defaultValues } = formState;
  const { required: requiredFieldControlDefaultValue } = defaultValues;

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldControlNames.OPTIONS,
  });
  const options = useWatch({
    control,
    name: fieldControlNames.OPTIONS,
  });

  const validateRequiredField = createRequiredValidator(i18next.t('This field is required'));

  const optionsPlaceholder = Array.isArray(placeholders[OPTIONS_INPUT])
    ? placeholders[OPTIONS_INPUT]
    : [placeholders[OPTIONS_INPUT]];
  const firstEmptyOptionIndex = options.findIndex(({ option }) => !option.trim());

  const appendOption = () => {
    append({ option: '' });
  };

  const removeOption = (index) => {
    remove(index);
  };

  return (
    <Stack gap="spacing-m">
      <ControlGroup title={i18next.t('Label')} required>
        <InputGroupField
          name={fieldControlNames.LABEL}
          control={control}
          placeholder={placeholders[LABEL_INPUT]}
          rules={{
            validate: (value) =>
              validateRequiredField(value) || helpers.validatePersonalData(value),
          }}
        />
      </ControlGroup>
      <ControlGroup title={i18next.t('Options')} required>
        <div className="ws-option-fields">
          <Stack gap="spacing-3-xs">
            {fields.map(({ id }, index) => (
              <HorizontalStack key={id} gap="spacing-3-xs">
                <div className="ws-option-fields__input">
                  <InputGroupField
                    name={`${fieldControlNames.OPTIONS}.${index}.option`}
                    control={control}
                    placeholder={optionsPlaceholder[index % optionsPlaceholder.length]}
                    rules={{
                      validate: index === firstEmptyOptionIndex && validateRequiredField,
                    }}
                  />
                </div>
                <div className="ws-option-fields__icon">
                  <ActionButton
                    format="text"
                    iconGlyph="trash"
                    disabled={fields.length <= minOptionsCount}
                    onClick={() => removeOption(index)}
                  />
                </div>
              </HorizontalStack>
            ))}
          </Stack>
          <Box marginTop="spacing-2-xs">
            <ActionButton label={i18next.t('Add option')} onClick={appendOption} />
          </Box>
        </div>
      </ControlGroup>
      {typeof requiredFieldControlDefaultValue === 'boolean' && (
        <CheckboxField
          name={fieldControlNames.REQUIRED}
          control={control}
          label={i18next.t('Make required')}
          onChange={onChangeRequiredFieldControl}
        />
      )}
    </Stack>
  );
};

OptionsFieldControls.propTypes = {
  control: PropTypes.shape({}).isRequired,
  placeholders: PropTypes.shape({
    [LABEL_INPUT]: PropTypes.string,
    [PLACEHOLDER_INPUT]: PropTypes.string,
  }).isRequired,
  minOptionsCount: PropTypes.number.isRequired,
  onChangeRequiredFieldControl: PropTypes.func,
};

export default OptionsFieldControls;
